import React, { useEffect, useContext, useState, createContext } from "react";
import axios from "axios";
import { getSlug, getDeviceKey } from "../tenant";
import { initAnalytics } from "../Services/Analytics";

// URLs are env-driven so the same code runs in local dev and production.
// Defaults below are the production values, used when the REACT_APP_* vars
// are absent (i.e. a plain `npm run build`). Local dev overrides them via
// frontend/.env.development (see DEVELOPMENT.md). One build serves every
// client — the tenant is chosen by the URL slug, not by the API domain.
const configModel = {
    baseURL: process.env.REACT_APP_BASE_URL || "https://magicfolio.media/",
    baseURLAssets: process.env.REACT_APP_ASSETS_URL || "https://magicfoliobucket.s3.sa-east-1.amazonaws.com/public/",
    baseURLAPI: process.env.REACT_APP_API_URL || "https://magicfolio.media/api",
};

// Fallback colors — mirror ConfigController::COLOR_DEFAULTS so the app looks
// right even before /v1/config resolves or if it fails.
const defaultBackground = { color: "#141414", gradient1: "#949EFF", gradient2: "#FF1A51" };
const defaultFontColor = "#FFFFFF";
// Mirrors ConfigController::DEFAULT_FONT_FAMILY — the app's original hardcoded font.
const defaultFontFamily = "DM Sans";
// Mirrors ConfigController::DEFAULT_FONT_FAMILY_SECONDARY — the accent/mono font.
const defaultFontFamilySecondary = "Space Mono";

// The theme colors are cached in localStorage so a reload paints the configured
// colors immediately — including the loading/error screens, which render before
// /v1/config comes back.
const THEME_CACHE_KEY = "magicFolioTheme";

function readThemeCache() {
    try {
        const t = JSON.parse(localStorage.getItem(THEME_CACHE_KEY));
        return t && t.background ? t : null;
    } catch (e) {
        return null;
    }
}

function writeThemeCache(background, fontColor, fontFamily, fontFamilySecondary) {
    try {
        localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ background, fontColor, fontFamily, fontFamilySecondary }));
    } catch (e) {
        /* ignore quota / disabled storage */
    }
}

const cachedTheme = readThemeCache();

// App configuration loaded once from /v1/config at boot (media, colors, entry
// routing). Colors seed from the cache so first paint already matches.
const appConfigModel = {
    loaded: false,
    error: false,
    media: null,
    logo: null,
    startButtonLabel: "Start",
    startButtonCss: "",
    background: (cachedTheme && cachedTheme.background) || defaultBackground,
    fontColor: (cachedTheme && cachedTheme.fontColor) || defaultFontColor,
    fontFamily: (cachedTheme && cachedTheme.fontFamily) || defaultFontFamily,
    fontFamilySecondary: (cachedTheme && cachedTheme.fontFamilySecondary) || defaultFontFamilySecondary,
    empreendimentos_count: 0,
    detail_path: null,
    plantasMultiply: false,
    // Kiosk timing from the admin Config page (seconds).
    idleTimeoutSeconds: 600,
    sessionGapSeconds: 90,
    // URL of the tenant's offline basemap (PMTiles archive), null until built.
    mapTiles: null,
};

const ConfigContext = createContext();

export default function ConfigProvider({ children }) {
    const [configApp, setConfigApp] = useState(configModel);
    const [appConfig, setAppConfig] = useState(appConfigModel);

    useEffect(() => {
        const slug = getSlug();
        if (!slug) {
            // No client in the URL — nothing to load; the router shows a landing.
            setAppConfig((c) => ({ ...c, loaded: true, error: true }));
            return;
        }
        const device = getDeviceKey();
        // A failed bootstrap is retried when it looks transient (no answer, or
        // a 5xx): a box keeps trying for as long as it takes (a network blip at
        // boot, the service worker swapping versions mid-request), a visitor a
        // few times. A 404 is a real unknown tenant: the landing, no retry.
        const RETRY_MS = 5000;
        const VISITOR_RETRIES = 5;
        // status 0 = no answer / malformed answer (a service worker mid-update
        // has handed back a 200 with an empty body); >= 500 = server trouble.
        const fail = (attempt, status) => {
            // 429 = throttled (a box's own sync burst): wait, never the landing
            const transient = !status || status >= 500 || status === 429;
            if (transient && (device || attempt < VISITOR_RETRIES)) {
                setTimeout(() => load(attempt + 1), RETRY_MS);
                return;
            }
            setAppConfig((c) => ({ ...c, loaded: true, error: true }));
        };
        const load = (attempt) => axios
            .get(configModel.baseURLAPI + "/v1/" + slug + "/config", { params: device ? { device } : {} })
            .then(function (res) {
                if (res.data && res.data.success === true) {
                    const d = res.data.data || {};
                    const bg = d.background || {};
                    const background = {
                        color: bg.color || defaultBackground.color,
                        gradient1: bg.gradient1 || defaultBackground.gradient1,
                        gradient2: bg.gradient2 || defaultBackground.gradient2,
                    };
                    const fontColor = d.font_color || defaultFontColor;
                    const fontFamily = d.font_family || defaultFontFamily;
                    const fontFamilySecondary = d.font_family_secondary || defaultFontFamilySecondary;
                    const idleTimeoutSeconds = Number(d.idle_timeout_seconds) > 0 ? Number(d.idle_timeout_seconds) : 600;
                    const sessionGapSeconds = Number(d.session_gap_seconds) >= 0 ? Number(d.session_gap_seconds) : 90;

                    setAppConfig({
                        loaded: true,
                        error: false,
                        media: d.media || null,
                        logo: d.logo || null,
                        startButtonLabel: d.start_button_label || "Start",
                        startButtonCss: d.start_button_css || "",
                        background,
                        fontColor,
                        fontFamily,
                        fontFamilySecondary,
                        empreendimentos_count: d.empreendimentos_count || 0,
                        detail_path: d.detail_path || null,
                        plantasMultiply: d.plantas_multiply === true,
                        idleTimeoutSeconds,
                        sessionGapSeconds,
                        mapTiles: d.map_tiles || null,
                    });

                    writeThemeCache(background, fontColor, fontFamily, fontFamilySecondary);

                    // Turn on navigation metrics only when the client opted in.
                    initAnalytics(d.grano_enabled === true, {
                        gapSeconds: sessionGapSeconds,
                        idleSeconds: idleTimeoutSeconds,
                    });

                    // A Magic Folio box caches the whole tenant for offline use.
                    // Loaded on demand: sync -> Axios -> this module would be an
                    // import cycle, and Axios reads getConfig() at load time.
                    if (device) import("../sync").then((m) => m.startSync()).catch(() => {});
                } else {
                    // a 200 that is not the config payload (empty / truncated body)
                    fail(attempt, res && res.data && res.data.success === false ? res.status : 0);
                }
            })
            .catch(function (err) {
                fail(attempt, err && err.response ? err.response.status : 0);
            });
        load(0);
    }, []);

    return (
        <ConfigContext.Provider value={{ configApp, setConfigApp, appConfig }}>
            {children}
        </ConfigContext.Provider>
    );
}

export function useConfig() {
    const context = useContext(ConfigContext);
    if (!context) throw new Error("useConfig must be used within a ConfigProvider");

    const { configApp, setConfigApp, appConfig } = context;

    return { configApp, setConfigApp, appConfig };
}

export function getConfig() {
    return configModel;
}
